/**
 * NarrateInMyVoice — inline button that plays a journal entry (answer + reflection)
 * in the user's cloned voice. Only renders when the user has a clone.
 * On-demand only (no autoplay). Caches audio via backend.
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Play, Pause, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NarrateInMyVoice({ entryId, className = "", size = "sm" }) {
  const [hasClone, setHasClone] = useState(null); // null=unknown, false=no, true=yes
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/voice/clone`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setHasClone(Boolean(d.has_clone));
        } else {
          if (!cancelled) setHasClone(false);
        }
      } catch {
        if (!cancelled) setHasClone(false);
      }
    })();
    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (!hasClone) return null;

  const toggle = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.play();
      setPlaying(true);
      return;
    }
    try {
      setLoading(true);
      const r = await fetch(`${API}/voice/journal/${entryId}/narrate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const text = await r.text();
        let msg = text;
        try {
          msg = JSON.parse(text).detail || text;
        } catch {}
        if (r.status === 429) {
          toast.error("Monthly voice cap reached. Resets on the 1st.");
        } else if (r.status === 402) {
          toast.error("Voice cloning requires a paid tier.");
        } else {
          toast.error(`Playback failed: ${String(msg).slice(0, 120)}`);
        }
        return;
      }
      const data = await r.json();
      const audio = new Audio(data.audio_url);
      audio.onended = () => setPlaying(false);
      audio.onpause = () => setPlaying(false);
      audio.onplay = () => setPlaying(true);
      audioRef.current = audio;
      audio.play();
      setPlaying(true);
    } catch (e) {
      toast.error("Playback failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={toggle}
      disabled={loading}
      data-testid={`narrate-my-voice-${entryId}`}
      variant="ghost"
      size={size}
      className={`text-terracotta hover:text-terracotta-deep hover:bg-terracotta/10 ${className}`}
      title="Play in my voice"
      aria-label="Play in my voice"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : playing ? (
        <Pause className="h-4 w-4" />
      ) : (
        <>
          <Sparkles className="h-3 w-3 mr-1" />
          <Play className="h-4 w-4" />
        </>
      )}
      <span className="ml-1 font-sans uppercase tracking-[0.2em] text-[0.65rem] hidden sm:inline">
        My voice
      </span>
    </Button>
  );
}
